import { computed, type ComputedRef } from 'vue'

import { useAccount, useAccountSession, useRbac } from '@/features/account'

/**
 * The KPI Tracking read gate. v3 declares it exactly once — on the sidebar leaf
 * (`DefaultLayout.vue:250–255`, `control:'Permission', id:['kpi_realized_values']`) — and forgets
 * it on the route (`router/routes/app.js:323–327` carries no `meta`, no `beforeEnter`), so the URL
 * is reachable by anyone who types it. v4 ports the DESIGN, not that hole: the same key gates the
 * nav leaf, `routes.ts` `meta.permission`, and this page-level `forbidden` state.
 */
export const SCORECARD_VIEW_PERMISSION = 'kpi_realized_values'

export interface UseScorecardRbac {
  /** Write affordances ("viewer hides writes", contract 10 §12). */
  canEdit: ComputedRef<boolean>
  /** Read access to the whole surface. False until RBAC is authoritative — see `rbacReady`. */
  canView: ComputedRef<boolean>
  /**
   * The RBAC map is authoritative. Pre-`ready` the account store defaults to `permissions={}`,
   * so a cold deep link would read as *denied* rather than *unknown*; the page renders its
   * loading state until this flips, and only then trusts `canView`. Mirrors the same rule in
   * `permissionGuard` (05), which passes pre-`ready` navigations through untouched.
   */
  rbacReady: ComputedRef<boolean>
}

/**
 * The single scorecards RBAC gate. `canEdit` keeps v3's semantics verbatim: v3 read
 * `accountStore.selectedAccountLevel !== 'viewer'`; the module-07 accessor for that exact field
 * is `useAccount().accountLevel` (contract 07 R10 — `useRbac()` carries the server rbac section,
 * not the caller's access level). The backend enforces viewer-for-reads / editor-for-writes
 * server-side; the FE mirrors it by omitting create/edit/delete controls for viewers.
 */
export function useScorecardRbac(): UseScorecardRbac {
  const { accountLevel } = useAccount()
  const rbac = useRbac()
  const session = useAccountSession()

  const canEdit = computed(() => accountLevel.value !== 'viewer')
  const rbacReady = computed(() => session.status.value === 'ready')
  const canView = computed(() => rbacReady.value && rbac.hasPermission(SCORECARD_VIEW_PERMISSION))

  return { canEdit, canView, rbacReady }
}
